/**
 * useConfirm — IMPLEMENTATION.md §12.
 *   confirm.open({title, body, danger, confirmLabel, cancelLabel}) → Promise<boolean>
 *   confirm.prompt({title, body, inputLabel, inputValue, confirmLabel}) → Promise<string | null>
 * Single host mounted in App.vue. open() resolves with true (confirm) or false
 * (cancel/dismiss). prompt() resolves with the typed text, or null on cancel.
 *
 * prompt() replaces window.prompt(), which the browser can suppress (dialog
 * suppression, background tab): it then returns null at once, so a button
 * that relied on it silently did nothing.
 */
import { reactive, readonly } from 'vue';

export interface ConfirmOptions {
  title: string;
  body?: string;
  danger?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Set by prompt(): the dialog shows a one-line text box with this label. */
  inputLabel?: string;
  /** Starting text for the box. */
  inputValue?: string;
}

interface ConfirmState {
  open: boolean;
  options: ConfirmOptions | null;
}

const state = reactive<ConfirmState>({ open: false, options: null });
let _resolver: ((ok: boolean, text: string) => void) | null = null;

function _show(opts: ConfirmOptions, resolver: (ok: boolean, text: string) => void): void {
  state.options = opts;
  state.open = true;
  _resolver = resolver;
}

function open(opts: ConfirmOptions): Promise<boolean> {
  return new Promise<boolean>((resolve) => { _show(opts, (ok) => resolve(ok)); });
}

function prompt(opts: ConfirmOptions & { inputLabel: string }): Promise<string | null> {
  return new Promise<string | null>((resolve) => { _show(opts, (ok, text) => resolve(ok ? text : null)); });
}

function answer(ok: boolean, text = ''): void {
  state.open = false;
  state.options = null;
  if (_resolver) {
    const r = _resolver;
    _resolver = null;
    r(ok, text);
  }
}

export const confirm = { open, prompt, answer };

export function useConfirm() {
  return { state: readonly(state), open, prompt, answer };
}
